import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { IS_PRERENDER } from "@/components/site/motionPresets";

/**
 * Shared dialog chrome for every modal on the site.
 *
 * Flat treatment: opaque backdrop with no blur, panel on surface-1 with a
 * line-strong hairline at radius.lg, no shadow. The enter/exit motion is kept
 * because it is functional feedback for an action, not decoration.
 *
 * Callers own the dialog's state and mount/unmount it inside <AnimatePresence>.
 * This shell owns focus, Escape, background isolation, and body scroll lock so
 * every modal follows one accessibility contract.
 */
export default function ModalShell({
  onClose,
  labelledBy,
  closeLabel,
  panelClassName = "border border-line-strong bg-surface-1",
  scrollClassName = "onevidaria-modal-scroll",
  maxWidthClassName = "max-w-2xl",
  children,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const appRoot = document.getElementById("root");
    const previousOverflow = document.body.style.overflow;
    const previousRootInert = appRoot?.inert ?? false;
    const previousAriaHidden = appRoot?.getAttribute("aria-hidden");

    document.body.style.overflow = "hidden";
    if (appRoot) {
      appRoot.inert = true;
      appRoot.setAttribute("aria-hidden", "true");
    }
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = [
        ...dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusIsInside = dialogRef.current.contains(document.activeElement);

      if (event.shiftKey && (!focusIsInside || document.activeElement === first)) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (!focusIsInside || document.activeElement === last)
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (appRoot) {
        appRoot.inert = previousRootInert;
        if (previousAriaHidden === null) appRoot.removeAttribute("aria-hidden");
        else appRoot.setAttribute("aria-hidden", previousAriaHidden);
      }
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={IS_PRERENDER ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        ref={dialogRef}
        tabIndex={-1}
        initial={IS_PRERENDER ? false : { opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className={`relative w-full ${maxWidthClassName} overflow-hidden rounded-lg p-2 text-left ${panelClassName}`}
      >
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-sm border border-line bg-surface-2 text-white/70 transition-colors hover:border-line-strong hover:text-white"
          aria-label={closeLabel}
        >
          <X size={17} />
        </button>

        <div
          className={`max-h-[calc(100vh-3rem)] overflow-y-auto rounded-md p-4 pr-5 sm:p-6 sm:pr-7 ${scrollClassName}`}
        >
          {children}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
